import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/components/theme/ThemeProvider';
import { AuthProvider } from '@/auth/authContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useDefaultRoute } from '@/hooks/useDefaultRoute';
import AdminLayout from '@/components/layout/AdminLayout';
import NotFoundPage from '@/pages/NotFoundPage';
import LandingPage from '@/pages/LandingPage';
import EnhancedLoginPage from '@/pages/EnhancedLoginPage';
import RegisterDriverPage from '@/pages/RegisterDriverPage';
import RegisterPassengerPage from '@/pages/RegisterPassengerPage';
import RegisterAdminPage from '@/pages/RegisterAdminPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import Dashboard from '@/pages/Dashboard';
import DriversPage from '@/pages/DriversPage';
import AddDriverPage from '@/pages/AddDriverPage';
import EditDriverPage from '@/pages/EditDriverPage';
import CreateDriverWizardPage from '@/pages/CreateDriverWizardPage';
import DriverDetailsPage from '@/pages/DriverDetailsPage';
import RidersPage from '@/pages/RidersPage';
import AddRiderPage from '@/pages/AddRiderPage';
import EditRiderPage from '@/pages/EditRiderPage';
import BasePricingPage from '@/pages/BasePricingPage';
import AddEditPricingPage from '@/pages/AddEditPricingPage';
import ViewBasePricingPage from '@/pages/ViewBasePricingPage';
import BasePricingAnalyticsPage from '@/pages/BasePricingAnalyticsPage';
import SurgePricingPage from '@/pages/SurgePricingPage';
import AddEditSurgePricingPage from '@/pages/AddEditSurgePricingPage';
import ViewSurgePricingPage from '@/pages/ViewSurgePricingPage';
import EditSurgePricingPage from '@/pages/EditSurgePricingPage';
import PromotionsPage from '@/pages/PromotionsPage';
import ViewPromotionPage from '@/pages/ViewPromotionPage';
import AddEditPromotionPage from '@/pages/AddEditPromotionPage';
import ReferralCampaignsPage from '@/pages/ReferralCampaignsPage';
import ViewReferralCampaignPage from '@/pages/ViewReferralCampaignPage';
import AddEditReferralCampaignPage from '@/pages/AddEditReferralCampaignPage';
import VehicleCategoriesPage from '@/pages/VehicleCategoriesPage';
import AddEditVehicleCategoryPage from '@/pages/AddEditVehicleCategoryPage';
import ViewVehicleCategoryPage from '@/pages/ViewVehicleCategoryPage';
import DisputesPage from '@/pages/DisputesPage';
import AddDisputePage from '@/pages/AddDisputePage';
import DistanceWaitTimeSettingsPage from '@/pages/DistanceWaitTimeSettingsPage';
import AuditLogsPage from '@/pages/AuditLogsPage';
import ReportsPage from '@/pages/ReportsPage';
// Hidden features - uncomment when ready
// import TripsPage from '@/pages/TripsPage';
// import TripDetailsPage from '@/pages/TripDetailsPage';
// import RadiusWaitTimeSettingsPage from '@/pages/settings/RadiusWaitTimeSettingsPage';
// import EmailTemplatesPage from '@/pages/settings/EmailTemplatesPage';
// import SmsTemplatesPage from '@/pages/settings/SmsTemplatesPage';
// import IntegrationSettingsPage from '@/pages/settings/IntegrationSettingsPage';

import RiderProfilePage from '@/pages/users/RiderProfilePage';
import DriverProfilePage from '@/pages/users/DriverProfilePage';
import DriverRideHistoryPage from '@/pages/DriverRideHistoryPage';
import RiderRideHistoryPage from '@/pages/RiderRideHistoryPage';import NotificationsPage from '@/pages/NotificationsPage';
import EmailSettingsPage from '@/pages/EmailSettingsPage';
import SMSSettingsPage from '@/pages/SMSSettingsPage';
import CountriesPage from '@/pages/geography/CountriesPage';
import StatesPage from '@/pages/geography/StatesPage';
import CitiesPage from '@/pages/geography/CitiesPage';
import UsersPage from '@/pages/users/UsersPage';
import AddUserPage from '@/pages/users/AddUserPage';
import EditUserPage from '@/pages/users/EditUserPage';
import RolesPage from '@/pages/users/RolesPage';
import AddEditRolePage from '@/pages/users/AddEditRolePage';
import PermissionsPage from '@/pages/users/PermissionsPage';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: 1,
    },
  },
});

/** Redirects to the first route the user has permission for */
const AdminIndexRedirect: React.FC = () => {
  const defaultRoute = useDefaultRoute();
  return <Navigate to={defaultRoute} replace />;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="aboosto-theme">
        <AuthProvider>
          <ErrorBoundary>
            <Router>
              <div className="min-h-screen bg-background text-foreground transition-colors">
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<EnhancedLoginPage />} />
                  <Route path="/login" element={<EnhancedLoginPage />} />
                  <Route path="/register/driver" element={<RegisterDriverPage />} />
                  <Route path="/register/passenger" element={<RegisterPassengerPage />} />
                  <Route path="/register/admin" element={<RegisterAdminPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />

                  {/* Protected Config Management Routes - Authentication Required (with AdminLayout) */}
                  <Route path="/admin/config" element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/admin/config/countries" replace />} />
                    <Route path="countries" element={<CountriesPage />} />
                    <Route path="states" element={<StatesPage />} />
                    <Route path="cities" element={<CitiesPage />} />
                  </Route>

                  {/* Protected Admin Routes */}
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<AdminIndexRedirect />} />
                    <Route path="dashboard" element={
                      <ProtectedRoute requiredPermission={{ resource: 'Dashboard', action: 'View' }}>
                        <Dashboard />
                      </ProtectedRoute>
                    } />
                    {/* Trips Management - HIDDEN until backend ready */}
                    {/* <Route path="trips">
                      <Route index element={<TripsPage />} />
                      <Route path=":id" element={<TripDetailsPage />} />
                    </Route> */}
                    <Route path="drivers">
                      <Route index element={<DriversPage />} />
                      <Route path="add" element={<AddDriverPage />} />
                      <Route path="create" element={<CreateDriverWizardPage />} />
                      <Route path="edit/:driverId" element={<EditDriverPage />} />
                      <Route path="profile/:id" element={<DriverProfilePage />} />
                      <Route path=":driverId/ride-history" element={<DriverRideHistoryPage />} />
                      <Route path=":id" element={<DriverDetailsPage />} />
                    </Route>
                    <Route path="riders">
                      <Route index element={<RidersPage />} />
                      <Route path="add" element={<AddRiderPage />} />
                      <Route path="edit/:riderId" element={<EditRiderPage />} />
                      <Route path="profile/:id" element={<RiderProfilePage />} />
                      <Route path=":riderId/ride-history" element={<RiderRideHistoryPage />} />
                    </Route>
                    <Route path="base-pricing">
                      <Route index element={<BasePricingPage />} />
                      <Route path="add" element={<AddEditPricingPage />} />
                      <Route path="edit/:pricingId" element={<AddEditPricingPage />} />
                      <Route path="view/:pricingId" element={<ViewBasePricingPage />} />
                      <Route path="analytics" element={<BasePricingAnalyticsPage />} />
                    </Route>
                    <Route path="vehicle-categories">
                      <Route index element={<VehicleCategoriesPage />} />
                      <Route path="add" element={<AddEditVehicleCategoryPage />} />
                      <Route path="edit/:categoryId" element={<AddEditVehicleCategoryPage />} />
                      <Route path="view/:categoryId" element={<ViewVehicleCategoryPage />} />
                    </Route>
                    <Route path="surge-pricing">
                      <Route index element={<SurgePricingPage />} />
                      <Route path="add" element={<AddEditSurgePricingPage />} />
                      <Route path="view/:id" element={<ViewSurgePricingPage />} />
                      <Route path="edit/:id" element={<EditSurgePricingPage />} />
                    </Route>
                    <Route path="promotions">
                      <Route index element={<PromotionsPage />} />
                      <Route path="add" element={<AddEditPromotionPage />} />
                      <Route path="view/:id" element={<ViewPromotionPage />} />
                      <Route path="edit/:id" element={<AddEditPromotionPage />} />
                      {/* TODO: Add PromotionsAnalyticsPage */}
                    </Route>
                    <Route path="referral-campaigns">
                      <Route index element={<ReferralCampaignsPage />} />
                      <Route path="add" element={<AddEditReferralCampaignPage />} />
                      <Route path="view/:id" element={<ViewReferralCampaignPage />} />
                      <Route path="edit/:id" element={<AddEditReferralCampaignPage />} />
                      {/* TODO: Add ReferralCampaignsAnalyticsPage */}
                    </Route>
                    <Route path="disputes">
                      <Route index element={<DisputesPage />} />
                      <Route path="add" element={<AddDisputePage />} />
                    </Route>
                    <Route path="reports" element={<ReportsPage />} />

                    {/* User Management */}
                    <Route path="users">
                      <Route index element={<Navigate to="/admin/users/list" replace />} />
                      <Route
                        path="list"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'Users', action: 'View' }}>
                            <UsersPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="add"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'Users', action: 'Create' }}>
                            <AddUserPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="edit/:userId"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'Users', action: 'Edit' }}>
                            <EditUserPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="roles"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'Roles', action: 'View' }}>
                            <RolesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="roles/add"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'Roles', action: 'Create' }}>
                            <AddEditRolePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="roles/edit/:id"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'Roles', action: 'Edit' }}>
                            <AddEditRolePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="permissions"
                        element={
                          <ProtectedRoute requiredPermission={{ resource: 'Permissions', action: 'View' }}>
                            <PermissionsPage />
                          </ProtectedRoute>
                        }
                      />
                    </Route>

                    {/* Security - Change Password is always accessible */}
                    <Route path="security">
                      <Route index element={<Navigate to="/admin/security/change-password" replace />} />
                      <Route path="change-password" element={<ChangePasswordPage />} />
                    </Route>

                    {/* Settings */}
                    <Route path="distance-wait-time-settings" element={<DistanceWaitTimeSettingsPage />} />
                    <Route path="audit-logs" element={<AuditLogsPage />} />
                    <Route path="notifications" element={<NotificationsPage />} />
                    <Route path="settings/email" element={<EmailSettingsPage />} />
                    <Route path="settings/sms" element={<SMSSettingsPage />} />
                    {/* Hidden settings - uncomment when ready */}
                    {/* <Route path="radius-wait-time-settings" element={<RadiusWaitTimeSettingsPage />} /> */}
                    {/* <Route path="settings/email-templates" element={<EmailTemplatesPage />} /> */}
                    {/* <Route path="settings/sms-templates" element={<SmsTemplatesPage />} /> */}
                    {/* <Route path="settings/integrations" element={<IntegrationSettingsPage />} /> */}
                  </Route>

                  {/* Driver Routes (to be implemented) */}
                  <Route path="/driver" element={
                    <ProtectedRoute requiredRole="driver" fallbackPath="/login">
                      <div>Driver Dashboard - Coming Soon</div>
                    </ProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/driver/dashboard" replace />} />
                    <Route path="dashboard" element={<div>Driver Dashboard - Coming Soon</div>} />
                  </Route>

                  {/* Passenger Routes (to be implemented) */}
                  <Route path="/passenger" element={
                    <ProtectedRoute requiredRole="passenger" fallbackPath="/login">
                      <div>Passenger Dashboard - Coming Soon</div>
                    </ProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/passenger/dashboard" replace />} />
                    <Route path="dashboard" element={<div>Passenger Dashboard - Coming Soon</div>} />
                  </Route>

                  {/* Legacy Routes (redirect to new paths) */}
                  <Route path="/register/rider" element={<Navigate to="/register/passenger" replace />} />
                  <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="/drivers" element={<Navigate to="/admin/drivers" replace />} />
                  <Route path="/passengers" element={<Navigate to="/admin/riders" replace />} />
                  <Route path="/riders" element={<Navigate to="/admin/riders" replace />} />
                  <Route path="/fares" element={<Navigate to="/admin/base-pricing" replace />} />
                  <Route path="/pricing" element={<Navigate to="/admin/base-pricing" replace />} />
                  <Route path="/disputes" element={<Navigate to="/admin/disputes" replace />} />
                  <Route path="/analytics" element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="/reports" element={<Navigate to="/admin/reports" replace />} />

                  {/* 404 Not Found Route */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                <Toaster />
              </div>
            </Router>
          </ErrorBoundary>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;